import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  FileText, 
  User, 
  Users, 
  DollarSign, 
  Target,
  Shield,
  Plus,
  Trash2,
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  Loader2,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';

function useIsMobile() {
  const [m, setM] = useState(false);
  useEffect(() => { const c = () => setM(window.innerWidth < 768); c(); window.addEventListener('resize', c); return () => window.removeEventListener('resize', c); }, []);
  return m;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CreateProfessionalPlan({ user, onLogout }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressStep, setProgressStep] = useState('');
  const [trialExpired, setTrialExpired] = useState(false);
  const [existingClient, setExistingClient] = useState(null);
  const [checkingClient, setCheckingClient] = useState(false);
  const [clientLimitReached, setClientLimitReached] = useState(false);
  const [usageInfo, setUsageInfo] = useState(null);
  const isMobile = useIsMobile();

  // Check trial status on mount
  useEffect(() => {
    checkTrialStatus();
    fetchUsageInfo();
  }, []);

  // Pre-fill client info if coming from client-specific plans page
  useEffect(() => {
    const clientName = searchParams.get('client');
    const clientEmail = searchParams.get('clientEmail');
    
    if (clientEmail) {
      // Preferred: Pre-fill email (will fetch name automatically)
      setFormData(prev => ({
        ...prev,
        client_email: clientEmail
      }));
      // Check if this client already exists
      checkExistingClient(clientEmail);
    } else if (clientName) {
      // Fallback: Pre-fill name only
      setFormData(prev => ({
        ...prev,
        client_name: clientName
      }));
      toast.success(`Creating plan for ${clientName}`);
    }
  }, [searchParams]);

  // Scroll to top whenever step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  const checkTrialStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/advisor/trial-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.is_expired) {
        setTrialExpired(true);
        // Redirect to pricing after a moment so user sees the message
        setTimeout(() => {
          navigate('/advisor/pricing?expired=true');
        }, 2000);
      }
    } catch (error) {
      console.error('Error checking trial status:', error);
    }
  };

  const fetchUsageInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/advisor/usage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsageInfo(response.data);
    } catch (error) {
      console.error('Error fetching usage info:', error);
    }
  };

  const checkExistingClient = async (email) => {
    if (!email || !email.includes('@')) return;
    
    setCheckingClient(true);
    setClientLimitReached(false);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/advisor/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const clients = response.data.clients || [];
      const client = clients.find(c => c.email === email);
      
      if (client) {
        setExistingClient(client);
        setClientLimitReached(false); // Existing client, no limit issue
        // Auto-fill the name from existing client
        setFormData(prev => ({
          ...prev,
          client_name: client.name,
          age: client.age || prev.age
        }));
        toast.success(`Creating plan for existing client: ${client.name}`);
      } else {
        setExistingClient(null);
        // Check if we can add a new client
        if (usageInfo && usageInfo.clients_remaining <= 0) {
          setClientLimitReached(true);
          toast.error(`Client limit reached (${usageInfo.clients_count}/${usageInfo.client_limit}). Upgrade to add new clients.`);
        }
      }
    } catch (error) {
      console.error('Error checking existing client:', error);
    } finally {
      setCheckingClient(false);
    }
  };
  
  const [formData, setFormData] = useState({
    // Client Basic Info
    client_name: '',
    client_email: '',
    age: '',
    spouse_age: '',
    children: [],
    occupation: '',
    
    // Financial Info
    monthly_income: '',
    monthly_expenses: '',
    current_savings: '',
    current_investments: '',
    
    // Insurance
    life_insurance_cover: '',
    health_insurance_cover: '',
    
    // Risk Profile
    risk_profile: 'moderate',
    
    // Retirement Planning
    retirement_age: '60',
    life_expectancy: '85',
    
    // Goals (dynamic array)
    goals: [
      {
        name: '',
        type: 'education',
        target_amount: '',
        years_to_goal: '',
        priority: 'medium'
      }
    ]
  });

  const goalTypes = [
    { value: 'education', label: 'Child Education', icon: '🎓' },
    { value: 'home', label: 'Home Purchase', icon: '🏠' },
    { value: 'marriage', label: 'Marriage/Wedding', icon: '💒' },
    { value: 'vacation', label: 'Dream Vacation', icon: '✈️' },
    { value: 'business', label: 'Business/Startup', icon: '💼' },
    { value: 'other', label: 'Other Goal', icon: '🎯' }
  ];

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Format number with Indian comma separators (e.g., 10,00,000)
  const formatNumberWithCommas = (value) => {
    if (!value) return '';
    // Remove all non-digit characters
    const numericValue = value.toString().replace(/[^0-9]/g, '');
    if (!numericValue) return '';
    // Format with Indian locale (lakhs, crores)
    return new Intl.NumberFormat('en-IN').format(parseInt(numericValue, 10));
  };

  // Parse formatted string back to raw number for storage
  const parseFormattedNumber = (formattedValue) => {
    if (!formattedValue) return '';
    return formattedValue.replace(/,/g, '');
  };

  // Handle currency input change - stores raw number, displays formatted
  const handleCurrencyInputChange = (name, value) => {
    const rawValue = parseFormattedNumber(value);
    setFormData({ ...formData, [name]: rawValue });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Check for existing client when email changes
    if (name === 'client_email' && value.includes('@')) {
      checkExistingClient(value);
    }
  };

  const addGoal = () => {
    setFormData({
      ...formData,
      goals: [
        ...formData.goals,
        {
          name: '',
          type: 'other',
          target_amount: '',
          years_to_goal: '',
          priority: 'medium'
        }
      ]
    });
  };

  const removeGoal = (index) => {
    const newGoals = formData.goals.filter((_, i) => i !== index);
    setFormData({ ...formData, goals: newGoals });
  };

  const updateGoal = (index, field, value) => {
    const newGoals = [...formData.goals];
    // For target_amount, strip commas before storing
    if (field === 'target_amount') {
      newGoals[index][field] = parseFormattedNumber(value);
    } else {
      newGoals[index][field] = value;
    }
    setFormData({ ...formData, goals: newGoals });
  };

  const addChild = () => {
    setFormData({
      ...formData,
      children: [...formData.children, { name: '', age: '' }]
    });
  };

  const removeChild = (index) => {
    const newChildren = formData.children.filter((_, i) => i !== index);
    setFormData({ ...formData, children: newChildren });
  };

  const updateChild = (index, field, value) => {
    const newChildren = [...formData.children];
    newChildren[index][field] = value;
    setFormData({ ...formData, children: newChildren });
  };

  // Simulate progress during plan generation
  useEffect(() => {
    if (generating) {
      const steps = [
        { progress: 20, text: 'Analyzing financial data...' },
        { progress: 40, text: 'Calculating retirement corpus...' },
        { progress: 60, text: 'Optimizing tax savings...' },
        { progress: 80, text: 'Generating recommendations...' },
        { progress: 95, text: 'Finalizing plan...' }
      ];

      let currentStep = 0;
      const interval = setInterval(() => {
        if (currentStep < steps.length) {
          setProgress(steps[currentStep].progress);
          setProgressStep(steps[currentStep].text);
          currentStep++;
        }
      }, 600);

      return () => clearInterval(interval);
    } else {
      setProgress(0);
      setProgressStep('');
    }
  }, [generating]);

  // Validation functions for each step
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateStep1 = () => {
    if (!formData.client_email?.trim()) {
      toast.error('Please enter client email');
      return false;
    }
    if (!isValidEmail(formData.client_email)) {
      toast.error('Please enter a valid email address');
      return false;
    }
    if (!formData.client_name?.trim()) {
      toast.error('Please enter client name');
      return false;
    }
    if (!formData.age || formData.age < 18 || formData.age > 100) {
      toast.error('Please enter a valid age (18-100)');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.monthly_income || formData.monthly_income <= 0) {
      toast.error('Please enter monthly income');
      return false;
    }
    if (formData.monthly_expenses === '' || formData.monthly_expenses === undefined || formData.monthly_expenses === null) {
      toast.error('Please enter monthly expenses');
      return false;
    }
    if (formData.current_savings === '' || formData.current_savings === undefined || formData.current_savings === null) {
      toast.error('Please enter current savings (enter 0 if none)');
      return false;
    }
    if (formData.current_investments === '' || formData.current_investments === undefined || formData.current_investments === null) {
      toast.error('Please enter current investments value (enter 0 if none)');
      return false;
    }
    if (formData.life_insurance_cover === '' || formData.life_insurance_cover === undefined || formData.life_insurance_cover === null) {
      toast.error('Please enter life insurance cover (enter 0 if none)');
      return false;
    }
    if (formData.health_insurance_cover === '' || formData.health_insurance_cover === undefined || formData.health_insurance_cover === null) {
      toast.error('Please enter health insurance cover (enter 0 if none)');
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    // Goals are optional, but if added they should be valid
    return true;
  };

  const handleNextStep = () => {
    let isValid = false;
    
    switch (step) {
      case 1:
        isValid = validateStep1();
        break;
      case 2:
        isValid = validateStep2();
        break;
      case 3:
        isValid = validateStep3();
        break;
      default:
        isValid = true;
    }
    
    if (isValid) {
      setStep(step + 1);
    }
  };

  const handleSubmit = async () => {
    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/advisor/create-professional-plan`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setProgress(100);
      setProgressStep('Plan generated successfully!');
      await new Promise(resolve => setTimeout(resolve, 500));
      toast.success('Financial plan generated successfully!');
      navigate(`/advisor/plan/${response.data.plan_id}`);
    } catch (error) {
      console.error('Error generating plan:', error);
      // Show specific error message from backend if available
      const errorMessage = error.response?.data?.detail || 'Failed to generate plan. Please try again.';
      toast.error(errorMessage);
    } finally {
      setGenerating(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4 flex items-center">
        <User className="w-6 h-6 mr-2 text-blue-600" />
        Client Information
      </h2>
      
      {/* Client Limit Warning */}
      {clientLimitReached && !existingClient && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-700 dark:text-red-400 font-medium">
            ⚠️ Client limit reached ({usageInfo?.clients_count}/{usageInfo?.client_limit})
          </p>
          <p className="text-red-600 dark:text-red-500 text-sm mt-1">
            You cannot add new clients. Upgrade your plan or create plans for existing clients.
          </p>
          <Button 
            className="mt-3 bg-red-600 hover:bg-red-700"
            onClick={() => navigate('/advisor/pricing')}
          >
            Upgrade Plan
          </Button>
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Label htmlFor="client_email">Email Address</Label>
          <Input
            id="client_email"
            name="client_email"
            type="email"
            value={formData.client_email}
            onChange={handleInputChange}
            placeholder="john@example.com"
          />
          {checkingClient && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Checking for existing client...
            </p>
          )}
        </div>
        
        <div>
          <Label htmlFor="client_name">
            Client Name *
            {existingClient && (
              <span className="ml-2 text-xs text-blue-600 dark:text-blue-400 font-normal">
                (Existing Client)
              </span>
            )}
          </Label>
          <Input
            id="client_name"
            name="client_name"
            value={formData.client_name}
            onChange={handleInputChange}
            placeholder="John Doe"
            required
            disabled={existingClient !== null}
            className={existingClient ? 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed' : ''}
          />
          {existingClient && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Client name cannot be changed when creating additional plans
            </p>
          )}
        </div>
        
        <div>
          <Label htmlFor="age">Age *</Label>
          <Input
            id="age"
            name="age"
            type="number"
            value={formData.age}
            onChange={handleInputChange}
            placeholder="35"
            required
          />
        </div>
        
        <div>
          <Label htmlFor="occupation">Occupation</Label>
          <Input
            id="occupation"
            name="occupation"
            value={formData.occupation}
            onChange={handleInputChange}
            placeholder="Software Engineer"
          />
        </div>
        
        <div>
          <Label htmlFor="spouse_age">Spouse Age (if applicable)</Label>
          <Input
            id="spouse_age"
            name="spouse_age"
            type="number"
            value={formData.spouse_age}
            onChange={handleInputChange}
            placeholder="32"
          />
        </div>
      </div>

      {/* Children Section */}
      <div className="mt-6">
        <div className="flex items-center justify-between mb-4">
          <Label className="text-lg">Children</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addChild}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Child
          </Button>
        </div>
        
        {formData.children.map((child, index) => (
          <div key={index} className="flex items-center gap-4 mb-3">
            <Input
              placeholder="Child's name"
              value={child.name}
              onChange={(e) => updateChild(index, 'name', e.target.value)}
              className="flex-1"
            />
            <Input
              placeholder="Age"
              type="number"
              value={child.age}
              onChange={(e) => updateChild(index, 'age', e.target.value)}
              className="w-24"
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => removeChild(index)}
              className="text-red-600"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4 flex items-center">
        <DollarSign className="w-6 h-6 mr-2 text-green-600" />
        Financial Information
      </h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Label htmlFor="monthly_income">Monthly Income *</Label>
          <Input
            id="monthly_income"
            name="monthly_income"
            type="text"
            inputMode="numeric"
            value={formatNumberWithCommas(formData.monthly_income)}
            onChange={(e) => handleCurrencyInputChange('monthly_income', e.target.value)}
            placeholder="1,50,000"
            required
          />
        </div>
        
        <div>
          <Label htmlFor="monthly_expenses">Monthly Expenses *</Label>
          <Input
            id="monthly_expenses"
            name="monthly_expenses"
            type="text"
            inputMode="numeric"
            value={formatNumberWithCommas(formData.monthly_expenses)}
            onChange={(e) => handleCurrencyInputChange('monthly_expenses', e.target.value)}
            placeholder="80,000"
            required
          />
        </div>
        
        <div>
          <Label htmlFor="current_savings">Current Savings/Cash *</Label>
          <Input
            id="current_savings"
            name="current_savings"
            type="text"
            inputMode="numeric"
            value={formatNumberWithCommas(formData.current_savings)}
            onChange={(e) => handleCurrencyInputChange('current_savings', e.target.value)}
            placeholder="5,00,000"
          />
        </div>
        
        <div>
          <Label htmlFor="current_investments">Current Investments Value *</Label>
          <Input
            id="current_investments"
            name="current_investments"
            type="text"
            inputMode="numeric"
            value={formatNumberWithCommas(formData.current_investments)}
            onChange={(e) => handleCurrencyInputChange('current_investments', e.target.value)}
            placeholder="10,00,000"
          />
        </div>
      </div>

      <div className="mt-8">
        <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4 flex items-center">
          <Shield className="w-5 h-5 mr-2 text-blue-600" />
          Insurance Coverage
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label htmlFor="life_insurance_cover">Life Insurance Cover *</Label>
            <Input
              id="life_insurance_cover"
              name="life_insurance_cover"
              type="text"
              inputMode="numeric"
              value={formatNumberWithCommas(formData.life_insurance_cover)}
              onChange={(e) => handleCurrencyInputChange('life_insurance_cover', e.target.value)}
              placeholder="50,00,000"
            />
          </div>
          
          <div>
            <Label htmlFor="health_insurance_cover">Health Insurance Cover *</Label>
            <Input
              id="health_insurance_cover"
              name="health_insurance_cover"
              type="text"
              inputMode="numeric"
              value={formatNumberWithCommas(formData.health_insurance_cover)}
              onChange={(e) => handleCurrencyInputChange('health_insurance_cover', e.target.value)}
              placeholder="10,00,000"
            />
          </div>
        </div>
      </div>

      <div className="mt-8">
        <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4 flex items-center">
          <Target className="w-5 h-5 mr-2 text-orange-600" />
          Retirement Planning
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label htmlFor="retirement_age">Expected Retirement Age</Label>
            <Input
              id="retirement_age"
              name="retirement_age"
              type="number"
              value={formData.retirement_age}
              onChange={handleInputChange}
              placeholder="60"
              min="40"
              max="80"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Typical: 55-65 years</p>
          </div>
          
          <div>
            <Label htmlFor="life_expectancy">Life Expectancy</Label>
            <Input
              id="life_expectancy"
              name="life_expectancy"
              type="number"
              value={formData.life_expectancy}
              onChange={handleInputChange}
              placeholder="85"
              min="60"
              max="100"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Average in India: 70-85 years</p>
          </div>
        </div>
        
        <div className="mt-3 p-3 bg-orange-50 dark:bg-orange-900/10 border border-orange-200 dark:border-orange-800 rounded-lg">
          <p className="text-xs text-orange-700 dark:text-orange-300">
            💡 Your retirement corpus will be automatically calculated based on your current expenses, inflation, 
            and the retirement timeline you specify above.
          </p>
        </div>
      </div>

      <div className="mt-8">
        <Label htmlFor="risk_profile" className="text-lg">Risk Profile</Label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
          {[
            { value: 'conservative', label: 'Conservative', desc: 'Low risk, stable returns' },
            { value: 'moderate', label: 'Moderate', desc: 'Balanced risk & returns' },
            { value: 'aggressive', label: 'Aggressive', desc: 'High risk, high returns' }
          ].map((profile) => (
            <Card
              key={profile.value}
              className={`p-4 cursor-pointer transition-all ${
                formData.risk_profile === profile.value
                  ? 'border-2 border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                  : 'border border-gray-200 dark:border-gray-700'
              }`}
              onClick={() => setFormData({ ...formData, risk_profile: profile.value })}
            >
              <h4 className="font-semibold text-gray-800 dark:text-gray-100 mb-1">{profile.label}</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">{profile.desc}</p>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 flex items-center">
          <Target className="w-6 h-6 mr-2 text-purple-600" />
          Financial Goals
        </h2>
        <Button
          type="button"
          variant="outline"
          onClick={addGoal}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Goal
        </Button>
      </div>

      <div className="space-y-4">
        {formData.goals.map((goal, index) => (
          <Card key={index} className="p-6 dark:bg-gray-800">
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                Goal {index + 1}
              </h3>
              {formData.goals.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeGoal(index)}
                  className="text-red-600"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Goal Type</Label>
                <select
                  className="w-full mt-1 p-2 border rounded-md dark:bg-gray-700 dark:border-gray-600"
                  value={goal.type}
                  onChange={(e) => updateGoal(index, 'type', e.target.value)}
                >
                  {goalTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.icon} {type.label}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <Label>Goal Name</Label>
                <Input
                  value={goal.name}
                  onChange={(e) => updateGoal(index, 'name', e.target.value)}
                  placeholder="e.g., Child's MBA Education"
                />
              </div>
              
              <div>
                <Label>Target Amount (₹)</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={formatNumberWithCommas(goal.target_amount)}
                  onChange={(e) => updateGoal(index, 'target_amount', e.target.value)}
                  placeholder="20,00,000"
                />
              </div>
              
              <div>
                <Label>Years to Achieve</Label>
                <Input
                  type="number"
                  value={goal.years_to_goal}
                  onChange={(e) => updateGoal(index, 'years_to_goal', e.target.value)}
                  placeholder="10"
                />
              </div>
              
              <div>
                <Label>Priority</Label>
                <select
                  className="w-full mt-1 p-2 border rounded-md dark:bg-gray-700 dark:border-gray-600"
                  value={goal.priority}
                  onChange={(e) => updateGoal(index, 'priority', e.target.value)}
                >
                  <option value="high">🔴 High</option>
                  <option value="medium">🟡 Medium</option>
                  <option value="low">🟢 Low</option>
                </select>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4 flex items-center">
        <CheckCircle className="w-6 h-6 mr-2 text-green-600" />
        Review & Confirm
      </h2>
      
      <div className="p-4 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg mb-6">
        <p className="text-sm text-blue-700 dark:text-blue-300">
          ✓ Please review all information before generating the financial plan. You can go back to edit any section.
        </p>
      </div>

      {/* Client Information Summary */}
      <Card className="p-6 dark:bg-gray-800">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 flex items-center">
            <User className="w-5 h-5 mr-2 text-blue-600" />
            Client Information
          </h3>
          <Button variant="ghost" size="sm" onClick={() => setStep(1)}>
            Edit
          </Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-gray-600 dark:text-gray-400">Name:</span>
            <p className="font-medium text-gray-800 dark:text-gray-100">{formData.client_name || 'N/A'}</p>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-400">Email:</span>
            <p className="font-medium text-gray-800 dark:text-gray-100">{formData.client_email || 'N/A'}</p>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-400">Age:</span>
            <p className="font-medium text-gray-800 dark:text-gray-100">{formData.age ? `${formData.age} years` : 'N/A'}</p>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-400">Occupation:</span>
            <p className="font-medium text-gray-800 dark:text-gray-100">{formData.occupation || 'N/A'}</p>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-400">Spouse Age:</span>
            <p className="font-medium text-gray-800 dark:text-gray-100">{formData.spouse_age ? `${formData.spouse_age} years` : 'N/A'}</p>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-400">Children:</span>
            <p className="font-medium text-gray-800 dark:text-gray-100">{formData.children.length}</p>
          </div>
        </div>
      </Card>

      {/* Financial Summary */}
      <Card className="p-6 dark:bg-gray-800">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 flex items-center">
            <DollarSign className="w-5 h-5 mr-2 text-green-600" />
            Financial Snapshot
          </h3>
          <Button variant="ghost" size="sm" onClick={() => setStep(2)}>
            Edit
          </Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-gray-600 dark:text-gray-400">Monthly Income:</span>
            <p className="font-medium text-gray-800 dark:text-gray-100">{formatCurrency(formData.monthly_income)}</p>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-400">Monthly Expenses:</span>
            <p className="font-medium text-gray-800 dark:text-gray-100">{formatCurrency(formData.monthly_expenses)}</p>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-400">Monthly Surplus:</span>
            <p className="font-medium text-green-600 dark:text-green-400">
              {formatCurrency(Number(formData.monthly_income || 0) - Number(formData.monthly_expenses || 0))}
            </p>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-400">Current Savings:</span>
            <p className="font-medium text-gray-800 dark:text-gray-100">{formatCurrency(formData.current_savings)}</p>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-400">Current Investments:</span>
            <p className="font-medium text-gray-800 dark:text-gray-100">{formatCurrency(formData.current_investments)}</p>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-400">Net Worth:</span>
            <p className="font-medium text-blue-600 dark:text-blue-400">
              {formatCurrency(Number(formData.current_savings || 0) + Number(formData.current_investments || 0))}
            </p>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-gray-600 dark:text-gray-400">Life Insurance:</span>
              <p className="font-medium text-gray-800 dark:text-gray-100">{formatCurrency(formData.life_insurance_cover)}</p>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Health Insurance:</span>
              <p className="font-medium text-gray-800 dark:text-gray-100">{formatCurrency(formData.health_insurance_cover)}</p>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Risk Profile:</span>
              <p className="font-medium text-gray-800 dark:text-gray-100 capitalize">{formData.risk_profile}</p>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Retirement Age:</span>
              <p className="font-medium text-gray-800 dark:text-gray-100">{formData.retirement_age} years</p>
            </div>
          </div>
        </div>
      </Card>

      {/* Goals Summary */}
      <Card className="p-6 dark:bg-gray-800">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 flex items-center">
            <Target className="w-5 h-5 mr-2 text-purple-600" />
            Financial Goals ({formData.goals.filter(g => g.name || g.target_amount).length})
          </h3>
          <Button variant="ghost" size="sm" onClick={() => setStep(3)}>
            Edit
          </Button>
        </div>
        <div className="space-y-3">
          {formData.goals.filter(g => g.name || g.target_amount).map((goal, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex-1">
                <p className="font-medium text-gray-800 dark:text-gray-100">
                  {goalTypes.find(t => t.value === goal.type)?.icon} {goal.name || 'Unnamed Goal'}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {formatCurrency(goal.target_amount)} in {goal.years_to_goal} years
                </p>
              </div>
              <span className={`text-xs px-2 py-1 rounded-full ${
                goal.priority === 'high' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                goal.priority === 'medium' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
              }`}>
                {goal.priority === 'high' ? '🔴' : goal.priority === 'medium' ? '🟡' : '🟢'} {goal.priority}
              </span>
            </div>
          ))}
          {formData.goals.filter(g => g.name || g.target_amount).length === 0 && (
            <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-4">No goals added</p>
          )}
        </div>
      </Card>
    </div>
  );

  // Loading overlay
  const renderLoadingOverlay = () => (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <Card className="p-8 max-w-md w-full mx-4">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 relative">
            <Loader2 className="w-16 h-16 text-blue-600 animate-spin" />
          </div>
          <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-2">
            Generating Your Financial Plan
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">{progressStep}</p>
          
          {/* Progress Bar */}
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 mb-2">
            <div 
              className="bg-blue-600 h-3 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">{progress}% Complete</p>
          
          <div className="mt-6 space-y-2 text-left">
            <div className="flex items-center text-sm">
              <CheckCircle className={`w-4 h-4 mr-2 ${progress >= 20 ? 'text-green-600' : 'text-gray-400'}`} />
              <span className={progress >= 20 ? 'text-green-600' : 'text-gray-500'}>Analyzing financial data</span>
            </div>
            <div className="flex items-center text-sm">
              <CheckCircle className={`w-4 h-4 mr-2 ${progress >= 40 ? 'text-green-600' : 'text-gray-400'}`} />
              <span className={progress >= 40 ? 'text-green-600' : 'text-gray-500'}>Calculating retirement corpus</span>
            </div>
            <div className="flex items-center text-sm">
              <CheckCircle className={`w-4 h-4 mr-2 ${progress >= 60 ? 'text-green-600' : 'text-gray-400'}`} />
              <span className={progress >= 60 ? 'text-green-600' : 'text-gray-500'}>Optimizing tax savings</span>
            </div>
            <div className="flex items-center text-sm">
              <CheckCircle className={`w-4 h-4 mr-2 ${progress >= 80 ? 'text-green-600' : 'text-gray-400'}`} />
              <span className={progress >= 80 ? 'text-green-600' : 'text-gray-500'}>Generating recommendations</span>
            </div>
            <div className="flex items-center text-sm">
              <CheckCircle className={`w-4 h-4 mr-2 ${progress >= 95 ? 'text-green-600' : 'text-gray-400'}`} />
              <span className={progress >= 95 ? 'text-green-600' : 'text-gray-500'}>Finalizing plan</span>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );

  return (
    <div data-testid="create-plan-page" className="min-h-screen p-4 lg:p-8 relative overflow-hidden">
      <Navigation user={user} onLogout={onLogout} />
      {!isMobile && (
        <div className="pointer-events-none fixed inset-0 z-0" aria-hidden="true">
          <motion.div className="absolute w-[400px] h-[400px] rounded-full bg-gradient-to-r from-blue-600/[0.06] to-indigo-500/[0.06] blur-3xl" animate={{ x: [0, 20, 0], y: [0, -15, 0] }} transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }} style={{ top: '5%', left: '5%' }} />
        </div>
      )}
      <motion.div className="max-w-5xl mx-auto relative z-10" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
        
        {/* Header */}
        {trialExpired ? (
          <Card className="p-8 text-center">
            <div className="text-red-600 text-6xl mb-4">
              <XCircle className="w-16 h-16 mx-auto text-red-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">
              Your Trial Has Ended
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              To create new financial plans, please choose a subscription plan.
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500">
              Redirecting to pricing page...
            </p>
          </Card>
        ) : (
          <>
            <motion.div className="mb-8" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <div className="flex items-center gap-3 mb-2">
                <motion.div className="w-2 h-8 rounded-full bg-gradient-to-b from-blue-500 to-indigo-400" initial={{ scaleY: 0 }} animate={{ scaleY: 1 }} transition={{ duration: 0.4, delay: 0.2 }} />
                <h1 data-testid="create-plan-header" className="text-3xl sm:text-4xl font-bold text-gray-800 dark:text-gray-100">
                  <span className="bg-gradient-to-r from-blue-500 to-indigo-400 bg-clip-text text-transparent">Create</span> Financial Plan
                </h1>
              </div>
              <p className="text-gray-600 dark:text-gray-400 ml-5 text-sm">
                Step {step} of 4 — {step === 1 ? 'Client Information' : step === 2 ? 'Financial Details' : step === 3 ? 'Goals & Objectives' : 'Review & Confirm'}
              </p>
            </motion.div>

        {/* Progress Bar */}
        <motion.div className="mb-8" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }}>
          <div className="flex items-center justify-between mb-2">
            {[1, 2, 3, 4].map((s) => (
              <div key={s} className="flex items-center flex-1">
                <motion.div
                  className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm ${
                    step > s
                      ? 'bg-green-500 text-white'
                      : step === s
                      ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-500'
                  }`}
                  animate={step === s ? { scale: [1, 1.1, 1] } : {}}
                  transition={{ duration: 0.5 }}
                >
                  {step > s ? <CheckCircle className="w-4 h-4" /> : s}
                </motion.div>
                {s < 4 && (
                  <div className="flex-1 h-1 mx-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-blue-600 rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: step > s ? '100%' : '0%' }}
                      transition={{ duration: 0.5 }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </motion.div>

        {/* Form Steps */}
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Card className="p-8 border-gray-200 dark:border-gray-800/60 mb-6">
              {step === 1 && renderStep1()}
              {step === 2 && renderStep2()}
              {step === 3 && renderStep3()}
              {step === 4 && renderStep4()}
            </Card>
          </motion.div>
        </AnimatePresence>

        {/* Navigation Buttons */}
        <motion.div className="flex items-center justify-between" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
          <Button
            data-testid="create-plan-back-btn"
            variant="outline"
            onClick={() => {
              if (step === 1) {
                navigate('/advisor/clients');
              } else {
                setStep(step - 1);
              }
            }}
            disabled={generating}
            className="border-gray-300 dark:border-gray-700"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {step === 1 ? 'Cancel' : 'Previous'}
          </Button>
          
          {step < 4 ? (
            <Button
              data-testid="create-plan-next-btn"
              onClick={handleNextStep}
              className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white shadow-lg shadow-blue-600/20"
              disabled={step === 1 && clientLimitReached && !existingClient}
            >
              {step === 3 ? 'Review Plan' : 'Next Step'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              data-testid="create-plan-generate-btn"
              onClick={handleSubmit}
              disabled={generating || (clientLimitReached && !existingClient)}
              className="bg-gradient-to-r from-green-600 to-emerald-500 hover:from-green-500 hover:to-emerald-400 text-white shadow-lg shadow-green-600/20"
            >
              {generating ? 'Generating...' : 'Generate Professional Plan'}
              <FileText className="w-4 h-4 ml-2" />
            </Button>
          )}
        </motion.div>

          </>
        )}
      </motion.div>
      <Footer />
      
      {/* Loading Overlay */}
      {generating && renderLoadingOverlay()}
    </div>
  );
}
